import React from 'react';
import {
  Box, Container, Typography, Grid, Card, CardContent,
  CardActionArea, Chip, Stack, Button
} from '@mui/material';
import { blueGrey, lightBlue, grey, blue } from '@mui/material/colors';
import FadeInSection from '../../components/anims/FadeInSection';

import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import PhoneIphoneOutlinedIcon from '@mui/icons-material/PhoneIphoneOutlined';
import CleaningServicesOutlinedIcon from '@mui/icons-material/CleaningServicesOutlined';
import WaterDropOutlinedIcon from '@mui/icons-material/WaterDropOutlined';

const MANUALS = [
  {
    icon: <CleaningServicesOutlinedIcon sx={{ fontSize: 40, color: blue[500] }} />,
    title: '다끌레어 청소카트',
    subtitle: '사용자 가이드',
    description: '청소카트 설치, 조립, 일상 관리 방법과 안전 주의사항을 안내합니다.',
    version: 'v2.1',
    updated: '2024. 08.',
    status: 'available',
    fileSize: '4.2MB',
    category: '제품',
  },
  {
    icon: <WaterDropOutlinedIcon sx={{ fontSize: 40, color: blue[500] }} />,
    title: '텀블러 세척기',
    subtitle: '사용자 가이드',
    description: '텀블러 세척기 설치, 세척 프로그램 설정, 위생 관리 방법을 안내합니다.',
    version: 'v1.3',
    updated: '2024. 06.',
    status: 'available',
    fileSize: '3.8MB',
    category: '제품',
  },
  {
    icon: <PhoneIphoneOutlinedIcon sx={{ fontSize: 40, color: lightBlue[600] }} />,
    title: '다끌레어 플랫폼',
    subtitle: '관리자 매뉴얼',
    description: 'ESG 데이터 입력, 보고서 생성, 대시보드 활용 방법을 안내합니다.',
    version: 'v1.0',
    updated: '2025. 01.',
    status: 'upcoming',
    category: '서비스',
  },
  {
    icon: <MenuBookOutlinedIcon sx={{ fontSize: 40, color: lightBlue[600] }} />,
    title: 'ENV-REPORT',
    subtitle: '작성 가이드',
    description: '환경 성과 보고서 작성 기준과 데이터 입력 방법을 상세하게 안내합니다.',
    version: 'v1.0',
    updated: '2025. 02.',
    status: 'upcoming',
    category: '서비스',
  },
];

export default function ManualsPage() {
  return (
    <Box sx={{ minHeight: 'calc(100vh - 64px)', backgroundColor: '#f9f9f9', py: 8 }}>
      <Container maxWidth="md">
        {/* 헤더 */}
        <FadeInSection delay={0}>
          <Box sx={{ textAlign: 'center', mb: 7 }}>
            <Chip
              label="고객지원"
              size="small"
              sx={{
                fontFamily: 'NotoSansKR',
                fontWeight: 700,
                fontSize: '11px',
                letterSpacing: '1px',
                backgroundColor: lightBlue[50],
                color: lightBlue[700],
                mb: 2,
              }}
            />
            <Typography
              sx={{
                fontFamily: 'NotoSansKR',
                fontWeight: 900,
                fontSize: { xs: '26px', md: '36px' },
                color: blueGrey[900],
                mb: 1.5,
              }}>
              사용설명서
            </Typography>
            <Typography
              sx={{
                fontFamily: 'NotoSansKR',
                fontWeight: 400,
                fontSize: '16px',
                color: blueGrey[500],
              }}>
              다끌레어 제품 및 서비스 사용설명서를 다운로드하세요
            </Typography>
          </Box>
        </FadeInSection>

        {/* 카테고리별 */}
        {['제품', '서비스'].map((category) => (
          <Box key={category} sx={{ mb: 6 }}>
            <FadeInSection delay={0}>
              <Typography
                sx={{
                  fontFamily: 'NotoSansKR',
                  fontWeight: 700,
                  fontSize: '18px',
                  color: blueGrey[700],
                  mb: 2,
                  pb: 1,
                  borderBottom: `2px solid ${blueGrey[100]}`,
                }}>
                {category}
              </Typography>
            </FadeInSection>
            <Grid container spacing={2.5}>
              {MANUALS.filter((m) => m.category === category).map((manual, i) => (
                <Grid size={{ xs: 12, sm: 6 }} key={manual.title}>
                  <FadeInSection delay={i * 0.1}>
                    <Card
                      elevation={0}
                      sx={{
                        borderRadius: '14px',
                        border: `1px solid ${grey[200]}`,
                        transition: 'all 0.2s',
                        opacity: manual.status === 'upcoming' ? 0.75 : 1,
                        '&:hover': manual.status === 'available' ? { boxShadow: 3, borderColor: blue[200], transform: 'translateY(-2px)' } : {},
                      }}>
                      <CardContent sx={{ px: 3, py: 3 }}>
                        <Stack direction="row" spacing={2} alignItems="flex-start">
                          <Box sx={{ flexShrink: 0, mt: 0.5 }}>{manual.icon}</Box>
                          <Stack spacing={1} sx={{ flex: 1 }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                              <Box>
                                <Typography
                                  sx={{
                                    fontFamily: 'NotoSansKR',
                                    fontWeight: 700,
                                    fontSize: '16px',
                                    color: blueGrey[900],
                                    textAlign: 'left',
                                  }}>
                                  {manual.title}
                                </Typography>
                                <Typography
                                  sx={{
                                    fontFamily: 'NotoSansKR',
                                    fontWeight: 400,
                                    fontSize: '13px',
                                    color: blueGrey[500],
                                    textAlign: 'left',
                                  }}>
                                  {manual.subtitle}
                                </Typography>
                              </Box>
                              <Chip
                                label={manual.status === 'upcoming' ? '준비중' : '다운로드'}
                                size="small"
                                sx={{
                                  fontFamily: 'NotoSansKR',
                                  fontWeight: 600,
                                  fontSize: '11px',
                                  backgroundColor: manual.status === 'upcoming' ? grey[200] : blue[50],
                                  color: manual.status === 'upcoming' ? grey[600] : blue[700],
                                  flexShrink: 0,
                                }}
                              />
                            </Stack>
                            <Typography
                              sx={{
                                fontFamily: 'NotoSansKR',
                                fontWeight: 400,
                                fontSize: '13px',
                                color: blueGrey[600],
                                lineHeight: 1.6,
                                textAlign: 'left',
                              }}>
                              {manual.description}
                            </Typography>
                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                              <Typography
                                sx={{
                                  fontFamily: 'NotoSansKR',
                                  fontSize: '11px',
                                  color: grey[500],
                                }}>
                                {manual.version} · 업데이트 {manual.updated}
                                {manual.fileSize && ` · ${manual.fileSize}`}
                              </Typography>
                              {manual.status === 'available' && (
                                <Button
                                  size="small"
                                  startIcon={<DownloadOutlinedIcon />}
                                  sx={{
                                    fontFamily: 'NotoSansKR',
                                    fontSize: '12px',
                                    color: blue[600],
                                    fontWeight: 600,
                                    '&:hover': { backgroundColor: blue[50] },
                                  }}>
                                  PDF 다운로드
                                </Button>
                              )}
                            </Stack>
                          </Stack>
                        </Stack>
                      </CardContent>
                    </Card>
                  </FadeInSection>
                </Grid>
              ))}
            </Grid>
          </Box>
        ))}
      </Container>
    </Box>
  );
}
